// Conversion settings, defaults, built-in presets and (de)serialisation for share links.

import type { CcLane, DrumKind } from './types';

export type QuantiseGrid = 'off' | '1/4' | '1/8' | '1/16' | '1/32' | '1/8T' | '1/16T' | '1/32T';

export interface CcLaneSetting {
  enabled: boolean;
  cc: number;
}

export interface Settings {
  // General
  subtune: number; // 0 = file default
  clock: 'auto' | 'PAL' | 'NTSC';
  durationMode: 'auto' | 'fixed';
  durationSeconds: number;
  loopDetection: boolean;
  tempoMode: 'auto' | 'manual';
  manualBpm: number;
  frameGrid: boolean;
  // Notes
  jumpThreshold: number;
  minNoteLength: number;
  hardRestart: boolean;
  transpose: number;
  voiceTranspose: [number, number, number];
  release: 'cut' | 'extend';
  // Arpeggios
  arpMode: 'notes' | 'chord' | 'root';
  arpWindow: number;
  arpMinReps: number;
  // Vibrato and slides
  vibratoMode: 'bend' | 'quantise' | 'ignore';
  bendRange: number;
  vibratoThreshold: number;
  slideMode: 'bend' | 'notes';
  // Drums
  drumsEnabled: boolean;
  drumOutput: 'separate' | 'voice';
  drumMap: Record<DrumKind, number>;
  // Velocity
  velocitySource: 'fixed' | 'sustain' | 'sustainVolume' | 'attack';
  fixedVelocity: number;
  velocityCurve: 'linear' | 'soft' | 'hard';
  velocityMin: number;
  velocityMax: number;
  // Quantisation
  quantise: QuantiseGrid;
  quantiseStrength: number;
  swing: number;
  // Controllers
  cc: Record<CcLane, CcLaneSetting>;
  ccMaxRate: number;
  // Tracks
  mergeVoices: boolean;
  ppq: number;
}

export const DEFAULT_SETTINGS: Settings = {
  subtune: 0,
  clock: 'auto',
  durationMode: 'auto',
  durationSeconds: 180,
  loopDetection: true,
  tempoMode: 'auto',
  manualBpm: 120,
  frameGrid: true,
  jumpThreshold: 0.75,
  minNoteLength: 1,
  hardRestart: true,
  transpose: 0,
  voiceTranspose: [0, 0, 0],
  release: 'cut',
  arpMode: 'notes',
  arpWindow: 4,
  arpMinReps: 2,
  vibratoMode: 'bend',
  bendRange: 2,
  vibratoThreshold: 20,
  slideMode: 'bend',
  drumsEnabled: true,
  drumOutput: 'separate',
  drumMap: { kick: 36, snare: 38, closedHat: 42, openHat: 46, tom: 45 },
  velocitySource: 'sustainVolume',
  fixedVelocity: 100,
  velocityCurve: 'linear',
  velocityMin: 40,
  velocityMax: 127,
  quantise: 'off',
  quantiseStrength: 100,
  swing: 0,
  cc: {
    cutoff: { enabled: true, cc: 74 },
    resonance: { enabled: true, cc: 71 },
    pulseWidth: { enabled: true, cc: 70 },
    waveform: { enabled: false, cc: 75 },
    volume: { enabled: false, cc: 7 },
  },
  ccMaxRate: 50,
  mergeVoices: false,
  ppq: 480,
};

export function cloneSettings(s: Settings): Settings {
  return JSON.parse(JSON.stringify(s)) as Settings;
}

/** Deep-merges a partial settings object (e.g. from JSON or a URL) over the defaults, dropping junk. */
export function mergeSettings(base: Settings, patch: unknown): Settings {
  const out = cloneSettings(base);
  const walk = (target: Record<string, unknown>, src: unknown) => {
    if (!src || typeof src !== 'object') return;
    for (const [k, v] of Object.entries(src as Record<string, unknown>)) {
      if (!(k in target)) continue;
      const cur = target[k];
      if (Array.isArray(cur)) {
        if (Array.isArray(v) && v.length === cur.length && v.every((x) => typeof x === typeof cur[0])) {
          target[k] = v.slice();
        }
      } else if (cur && typeof cur === 'object') {
        walk(cur as Record<string, unknown>, v);
      } else if (typeof v === typeof cur) {
        target[k] = v;
      }
    }
  };
  walk(out as unknown as Record<string, unknown>, patch);
  return out;
}

export interface Preset {
  id: string;
  name: string;
  description: string;
  settings: Settings;
}

const allCc = (enabled: boolean): Settings['cc'] => ({
  cutoff: { enabled, cc: 74 },
  resonance: { enabled, cc: 71 },
  pulseWidth: { enabled, cc: 70 },
  waveform: { enabled: false, cc: 75 },
  volume: { enabled: false, cc: 7 },
});

export const BUILTIN_PRESETS: Preset[] = [
  {
    id: 'default',
    name: 'Default',
    description:
      'Balanced starting point: fast arpeggio notes, bends, filter and pulse-width CCs, no quantisation.',
    settings: DEFAULT_SETTINGS,
  },
  {
    id: 'faithful',
    name: 'Faithful',
    description:
      'Minimal interpretation: fast arpeggio notes, bends for vibrato and slides, no quantisation.',
    settings: mergeSettings(DEFAULT_SETTINGS, {
      arpMode: 'notes',
      vibratoMode: 'bend',
      slideMode: 'bend',
      quantise: 'off',
      vibratoThreshold: 5,
      cc: { ...allCc(true), waveform: { enabled: true, cc: 75 }, volume: { enabled: true, cc: 7 } },
    }),
  },
  {
    id: 'musical',
    name: 'Musical',
    description: 'Arpeggios merged into chords, pitch bends kept, gently quantised to 1/16.',
    settings: mergeSettings(DEFAULT_SETTINGS, {
      arpMode: 'chord',
      vibratoMode: 'bend',
      slideMode: 'bend',
      quantise: '1/16',
      quantiseStrength: 75,
      minNoteLength: 2,
    }),
  },
  {
    id: 'clean',
    name: 'Clean for synths',
    description: 'Chords, no pitch bend or controllers, fully quantised – easy to edit and re-voice.',
    settings: mergeSettings(DEFAULT_SETTINGS, {
      arpMode: 'chord',
      vibratoMode: 'ignore',
      slideMode: 'notes',
      quantise: '1/16',
      quantiseStrength: 100,
      minNoteLength: 2,
      cc: allCc(false),
      velocitySource: 'fixed',
    }),
  },
];

/** Settings that require a new emulator capture when changed. */
export function captureKey(s: Settings, subtune: number): string {
  return JSON.stringify([subtune, s.clock, s.durationMode, s.durationSeconds, s.loopDetection]);
}

// ---------- share links ----------

function diff(base: unknown, value: unknown): unknown {
  if (JSON.stringify(base) === JSON.stringify(value)) return undefined;
  if (base && value && typeof base === 'object' && typeof value === 'object' && !Array.isArray(value)) {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as object)) {
      const d = diff((base as Record<string, unknown>)[k], (value as Record<string, unknown>)[k]);
      if (d !== undefined) out[k] = d;
    }
    return out;
  }
  return value;
}

function toBase64Url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(s: string): string {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
}

/** Encodes only the settings that differ from the defaults. Never contains the SID file. */
export function encodeSettings(s: Settings): string {
  const d = diff(DEFAULT_SETTINGS, s);
  return d === undefined ? '' : toBase64Url(JSON.stringify(d));
}

export function decodeSettings(encoded: string): Settings | null {
  try {
    return mergeSettings(DEFAULT_SETTINGS, JSON.parse(fromBase64Url(encoded)));
  } catch {
    return null;
  }
}
